import type { ECElementEvent } from 'echarts';
import ReactECharts from 'echarts-for-react';
import type { JSX } from 'react';

import { ChartDataItem, chartFont } from './charts.common';

export interface DonutChartProps {
    /** The title of the chart. */
    name: string;
    /** The data to display in the arcs of the chart (one array element per arc). */
    data: ChartDataItem[];
    /** The average calculated from `data`, displayed in the center of the chart. Because this may be a weighted average, it cannot be calculated by this component and is required as a prop. */
    average: number;
    /** The label for the average. Displayed immediately below the average. */
    averageLabel: string;
    /** The function to call when an arc is clicked. Will be called with the `name` of an element from `data`. */
    onChartClick: (item: string) => void;
}

/**
 * Similar to a pie chart, except arcs of a circle are used instead of slices of a circle.
 * Because arcs are used, the center is hollow. The center is used to display an average and a label for that average.
 */
export const DonutChart = (props: DonutChartProps): JSX.Element => {
    const primaryFont = getComputedStyle(document.documentElement).getPropertyValue(chartFont);

    const formattedAverage = props.average.toFixed(1);

    const ariaDescription = (() => {
        const categoriesAndValues = props.data.map((element) => `${element.value} ${element.name}`).join(', ');
        return `A donut chart titled "${props.name}". The average is ${formattedAverage} (${props.averageLabel}). There are ${categoriesAndValues}.`;
    })();

    return (
        <ReactECharts
            aria-label={ariaDescription}
            role="img"
            option={{
                tooltip: {
                    trigger: 'item',
                },
                legend: {
                    show: false,
                },
                textStyle: {
                    fontFamily: primaryFont,
                },
                grid: {
                    left: 0,
                    top: 0,
                    right: 0,
                    bottom: 0,
                },
                series: [
                    {
                        name: props.name,
                        type: 'pie',
                        radius: ['70%', '90%'],
                        avoidLabelOverlap: false,
                        label: {
                            show: false,
                            position: 'center',
                        },
                        labelLine: {
                            show: false,
                        },
                        emphasis: {
                            focus: 'item',
                        },
                        data: props.data.map(({ name, value, color }) => ({
                            name,
                            value,
                            itemStyle: { color },
                        })),
                    },
                ],
                graphic: {
                    elements: [
                        {
                            type: 'group',
                            left: 'center',
                            top: 'middle',
                            children: [
                                {
                                    type: 'text',
                                    bottom: 0,
                                    left: 'center',
                                    style: {
                                        text: formattedAverage,
                                        fontSize: 60,
                                        fontWeight: 'bold',
                                        fill: 'white',
                                        fontFamily: primaryFont,
                                    },
                                },
                                {
                                    type: 'text',
                                    top: 0,
                                    left: 'center',
                                    style: {
                                        text: props.averageLabel,
                                        fontSize: 20,
                                        fontWeight: 'bold',
                                        fill: 'white',
                                        fontFamily: primaryFont,
                                    },
                                },
                            ],
                        },
                    ],
                },
            }}
            style={{
                height: '100%',
                width: '100%',
                minHeight: '250px',
            }}
            onEvents={{ click: (params: ECElementEvent) => props.onChartClick(params.name) }}
        />
    );
};
